import {
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import request from 'src/utils/request';

const deepClone = obj => JSON.parse(JSON.stringify(obj || {}));

function PolicyCompareQcRemarks({
    modouleDetail,
    qcRemarkOfEachSections = null,
    setQcRemarkOfEachSections = null,
    section_key = '',
    block_key = '',
    checkAllModuleIsVerified,
    refreshKey = 0,
    sectionName = ''
}) {
    const lastSavedRef = useRef('');
    const activatedRef = useRef(false);
    const [loader, setLoader] = useState(false);

    let isStatusDisabled = false;
    if (section_key === 'coverages_requested') {
        isStatusDisabled =
            (qcRemarkOfEachSections?.coverages_requested?.unSelected_coverages?.length ?? 0) > 0;
    } else if (section_key === 'clauses_requested') {
        isStatusDisabled =
            (qcRemarkOfEachSections?.clauses_requested?.unSelected_clauses?.length ?? 0) > 0;
    }

    const RfqApprovalSchema = Yup.object().shape({
        status: Yup.string().required('Status is required'),
        remarks: Yup.string().when('status', {
            is: 'endorsement_required',
            then: schema => schema.required('Remarks is required'),
            otherwise: schema => schema.notRequired()
        })
    });

    const formik = useFormik({
        initialValues: {
            status: 'endorsement_required',
            remarks: '',
            isActive: true
        },
        validationSchema: RfqApprovalSchema,
        onSubmit: values => {
            if (!qcRemarkOfEachSections || !section_key) return;

            const signature = JSON.stringify({ section_key, block_key, ...values });
            if (lastSavedRef.current === signature) return;
            lastSavedRef.current = signature;

            setLoader(true);

            const next = deepClone(qcRemarkOfEachSections);

            if (block_key) {
                if (!next.sum_insured_sections) next.sum_insured_sections = {};
                next.sum_insured_sections[block_key] = {
                    ...(next.sum_insured_sections[block_key] || {}),
                    ...values,
                    isActive: true,
                    sectionName
                };
            } else {
                next[section_key] = {
                    ...(next[section_key] || {}),
                    ...values,
                    isActive: true,
                    sectionName
                };
            }

            setQcRemarkOfEachSections(next);
            checkAllModuleIsVerified();

            request({
                url: 'endorsement/updateEndorsementQcData',
                method: 'POST',
                data: {
                    id: modouleDetail.id,
                    qcData: next
                }
            })
                .then(() => {
                    setLoader(false);
                    // checkAllModuleIsVerified();
                })
                .catch(() => setLoader(false));
        }
    });

    /* ----------------------  ACTIVATE MODULE ON LOAD ---------------------- */

    useEffect(() => {
        if (!setQcRemarkOfEachSections || !section_key || activatedRef.current) return;

        activatedRef.current = true;

        setQcRemarkOfEachSections(prev => {
            const next = deepClone(prev || {});

            if (block_key) {
                if (!next.sum_insured_sections) next.sum_insured_sections = {};
                next.sum_insured_sections[block_key] = {
                    ...(next.sum_insured_sections[block_key] || {
                        status: 'endorsement_required',
                        remarks: ''
                    }),
                    isActive: true,
                    sectionName
                };
            } else {
                next[section_key] = {
                    ...(next[section_key] || {
                        status: 'endorsement_required',
                        remarks: ''
                    }),
                    isActive: true,
                    sectionName
                };
            }

            return next;
        });
    }, [section_key, block_key]);

    useEffect(() => {
        if (!block_key || !setQcRemarkOfEachSections) return;

        setQcRemarkOfEachSections(prev => {
            if (!prev) return prev;

            if (prev.sum_insured_sections?.[block_key]?.isActive) return prev;

            const next = JSON.parse(JSON.stringify(prev));

            if (!next.sum_insured_sections) next.sum_insured_sections = {};

            next.sum_insured_sections[block_key] = {
                status: next.sum_insured_sections?.[block_key]?.status || 'endorsement_required',
                remarks: next.sum_insured_sections?.[block_key]?.remarks || '',
                isActive: true,
                sectionName
            };

            return next;
        });
    }, [block_key]);


    /* ----------------------  SYNC FORM FROM STORE ---------------------- */

    useEffect(() => {
        if (!qcRemarkOfEachSections || !section_key) return;

        const sectionData = block_key
            ? qcRemarkOfEachSections?.sum_insured_sections?.[block_key]
            : qcRemarkOfEachSections?.[section_key];

        if (sectionData) {
            if (formik.values.status !== sectionData.status)
                formik.setFieldValue('status', sectionData.status || '');

            if (formik.values.remarks !== sectionData.remarks)
                formik.setFieldValue('remarks', sectionData.remarks || '');
        }
    }, [qcRemarkOfEachSections, refreshKey]);

    return (
        <Grid container sx={{ mt: 2 }}>
            <Grid item xs={6} pr={1}>
                <FormControl component="fieldset" disabled={isStatusDisabled}>
                    <Typography
                        variant="body2"
                        sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                        Status <span style={{ color: 'red' }}>*</span>
                    </Typography>

                    <RadioGroup
                        row
                        value={formik.values.status}
                        onChange={(e) => {
                            formik.setFieldValue('status', e.target.value);
                            formik.submitForm();
                            if (e.target.value == 'approve') {
                                formik.setFieldValue('remarks', '')
                            }
                        }}
                        onBlur={formik.submitForm}
                    >
                        <FormControlLabel
                            value="approve"
                            control={<Radio size="small" />}
                            label="Approve"
                        />

                        <FormControlLabel
                            value="endorsement_required"
                            control={<Radio size="small" />}
                            label="Endorsement Required"
                        />
                        {(section_key == 'suggestedDeductible' || section_key == 'warranties_exclusions') && <FormControlLabel
                            value="NA"
                            control={<Radio size="small" />}
                            label="N/A"
                        />}

                    </RadioGroup>
                </FormControl>

            </Grid>

            {formik.values.status == 'endorsement_required' && <Grid item xs={6} pl={1}>
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    label={
                        formik.values.status === 'endorsement_required' ? (
                            <span>
                                Remarks <span style={{ color: 'red' }}>*</span>
                            </span>
                        ) : (
                            'Remarks'
                        )
                    }
                    value={formik.values.remarks}
                    onChange={e => formik.setFieldValue('remarks', e.target.value)}
                    onBlur={formik.submitForm}
                    error={
                        formik.values.status === 'endorsement_required' &&
                        formik.touched.remarks &&
                        Boolean(formik.errors.remarks)
                    }
                    helperText={
                        formik.values.status === 'endorsement_required'
                            ? formik.touched.remarks && formik.errors.remarks
                            : ''
                    }
                />
            </Grid>}


        </Grid>
    );
}

export default PolicyCompareQcRemarks;
